import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { X, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatBot } from '@/contexts/ChatBotContext'
import { useBackgroundContrast } from '@/hooks/useBackgroundContrast'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { QuickSuggestions } from './QuickSuggestions'
import { quickSuggestions } from './config'
import type { ChatMessage as ChatMessageType, ChatState } from '@brochure/shared'

/**
 * HSA serif monogram — the brand mark inside the square launcher.
 * Sized to feel optical rather than mechanical against the square frame.
 */
function Monogram({ className }: { className?: string }) {
  return (
    <span
      className={cn('select-none leading-none', className)}
      style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 500,
        letterSpacing: '-0.04em',
      }}
      aria-hidden="true"
    >
      HSA
    </span>
  )
}

export function ChatBot() {
  const { t, i18n } = useTranslation('common')
  const { isOpen, setIsOpen, isHiddenByOverlay } = useChatBot()
  const [messages, setMessages] = useState<ChatMessageType[]>([])
  const [chatState, setChatState] = useState<ChatState>('idle')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const floatingButtonRef = useRef<HTMLButtonElement>(null)
  const background = useBackgroundContrast(floatingButtonRef, { containerRef })
  const isDarkBackground = background === 'dark'

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen])

  const generateId = () =>
    `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`

  const sendMessage = async (content: string) => {
    if (!content.trim() || chatState === 'loading') return

    const userMessage: ChatMessageType = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setChatState('loading')

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          history: messages.map((m) => ({ role: m.role, content: m.content })),
          language: i18n.language,
        }),
      })

      const data = await response.json()

      const assistantMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: data.response || data.error || t('chatbot.error'),
        timestamp: new Date(data.timestamp || Date.now()),
      }

      setMessages((prev) => [...prev, assistantMessage])
      setChatState('idle')
    } catch (error) {
      console.error('Chat error:', error)
      setChatState('error')

      const errorMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: t('chatbot.error'),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    }
  }

  const handleReset = () => {
    setMessages([])
    setChatState('idle')
  }

  const handleSuggestionSelect = (messageKey: string) => {
    const message = t(messageKey)
    sendMessage(message)
  }

  return (
    <div
      ref={containerRef}
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 transition-opacity duration-200"
      style={{
        zIndex: 9999,
        opacity: isHiddenByOverlay ? 0 : 1,
        pointerEvents: isHiddenByOverlay ? 'none' : 'auto',
        visibility: isHiddenByOverlay ? 'hidden' : 'visible',
      }}
      aria-hidden={isHiddenByOverlay}
    >
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              'absolute bottom-[4.5rem] sm:bottom-[5.25rem] right-0',
              'w-[calc(100vw-32px)] sm:w-[420px] max-w-[420px]',
              'bg-[var(--color-surface)] overflow-hidden',
              'flex flex-col',
              'max-h-[calc(100vh-100px)] sm:max-h-[calc(100vh-130px)]',
              'border border-[var(--color-border)]'
            )}
            style={{
              borderRadius: 'var(--radius-sm)',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            {/* Header — editorial: eyebrow + serif display title */}
            <div
              className="relative px-6 pt-5 pb-5"
              style={{
                background: 'var(--color-accent)',
                color: 'var(--color-on-accent)',
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <span
                    className="inline-flex items-center gap-3"
                    style={{
                      fontFamily: 'var(--font-body)',
                      fontSize: 'var(--text-caption)',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      letterSpacing: 'var(--tracking-eyebrow)',
                      color: 'var(--color-highlight)',
                    }}
                  >
                    {t('chatbot.eyebrow', { defaultValue: 'HSA Concierge' })}
                    <span
                      aria-hidden="true"
                      className="inline-block"
                      style={{
                        width: '2rem',
                        height: '1px',
                        background: 'var(--color-highlight)',
                        opacity: 0.7,
                      }}
                    />
                  </span>
                  <h3
                    className="mt-2 text-[1.5rem] leading-[1.1]"
                    style={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 500,
                      letterSpacing: 'var(--tracking-display)',
                      color: 'var(--color-on-accent)',
                    }}
                  >
                    {t('chatbot.title')}
                  </h3>
                  <p
                    className="mt-1.5 text-[0.8125rem]"
                    style={{
                      fontFamily: 'var(--font-body)',
                      color: 'var(--color-on-accent)',
                      opacity: 0.72,
                      lineHeight: 1.5,
                    }}
                  >
                    {t('chatbot.subtitle')}
                  </p>
                </div>
                <div className="flex items-center gap-1 -mr-1">
                  <button
                    onClick={handleReset}
                    className="p-2 transition-colors duration-[var(--duration-fast)] hover:bg-white/10"
                    style={{ borderRadius: 'var(--radius-sm)' }}
                    aria-label={t('chatbot.newConversation')}
                    title={t('chatbot.newConversation')}
                  >
                    <RotateCcw className="w-4 h-4 opacity-80" />
                  </button>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 transition-colors duration-[var(--duration-fast)] hover:bg-white/10"
                    style={{ borderRadius: 'var(--radius-sm)' }}
                    aria-label={t('chatbot.close')}
                  >
                    <X className="w-4 h-4 opacity-80" />
                  </button>
                </div>
              </div>

              {/* Champagne hairline foot */}
              <span
                aria-hidden="true"
                className="absolute left-0 right-0 bottom-0 h-px"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, var(--color-highlight) 20%, var(--color-highlight) 80%, transparent)',
                  opacity: 0.55,
                }}
              />
            </div>

            {/* Messages */}
            <div
              className="flex-1 overflow-y-auto px-5 py-5 space-y-4 min-h-[220px] max-h-[440px]"
              style={{ background: 'var(--color-bg)' }}
            >
              {messages.length === 0 ? (
                <div className="py-6">
                  <div className="text-center">
                    <span
                      style={{
                        fontFamily: 'var(--font-body)',
                        fontSize: 'var(--text-caption)',
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: 'var(--tracking-eyebrow)',
                        color: 'var(--color-muted)',
                      }}
                    >
                      {t('chatbot.welcomeEyebrow', { defaultValue: 'Benvenuto' })}
                    </span>
                    <p
                      className="mt-3 mb-7 mx-auto max-w-[28ch]"
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: '1.125rem',
                        lineHeight: 1.35,
                        letterSpacing: 'var(--tracking-display)',
                        color: 'var(--color-fg)',
                        fontWeight: 500,
                      }}
                    >
                      {t('chatbot.welcome')}
                    </p>
                  </div>
                  <QuickSuggestions
                    suggestions={quickSuggestions}
                    onSelect={handleSuggestionSelect}
                    disabled={chatState === 'loading'}
                  />
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <ChatMessage key={message.id} message={message} />
                  ))}
                  {chatState === 'loading' && (
                    <ChatMessage
                      message={{
                        id: 'typing',
                        role: 'assistant',
                        content: '',
                        timestamp: new Date(),
                      }}
                      isTyping
                    />
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <ChatInput
              ref={inputRef}
              onSend={sendMessage}
              disabled={chatState === 'loading'}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating launcher — square monogram, coherent with site CTAs */}
      <motion.button
        ref={floatingButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.96 }}
        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
        className={cn(
          'group relative w-14 h-14 sm:w-[3.75rem] sm:h-[3.75rem]',
          'flex items-center justify-center',
          'transition-[background,color,border,box-shadow] duration-[var(--duration-base)]',
          'border-2 border-[var(--color-fg)]',
          'hover:bg-[color-mix(in_srgb,var(--color-highlight)_28%,var(--color-bg))]',
          'hover:text-[var(--color-fg)]',
          isDarkBackground
            ? 'bg-[var(--color-surface)] text-[var(--color-fg)]'
            : 'bg-[var(--color-accent)] text-[var(--color-on-accent)]'
        )}
        style={{
          borderRadius: 'var(--radius-sm)',
          boxShadow: 'var(--shadow-lg)',
        }}
        aria-label={isOpen ? t('chatbot.close') : t('chatbot.open')}
      >
<AnimatePresence mode="wait" initial={false}>
          {isOpen ? (
            <motion.span
              key="close"
              initial={{ rotate: -45, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 45, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex"
            >
              <X className="w-5 h-5 sm:w-[1.375rem] sm:h-[1.375rem]" strokeWidth={1.5} />
            </motion.span>
          ) : (
            <motion.span
              key="mark"
              initial={{ y: 4, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -4, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex flex-col items-center justify-center"
            >
              <Monogram className="text-[1.0625rem] sm:text-[1.125rem]" />
              <span
                aria-hidden="true"
                className="mt-1 block"
                style={{
                  width: '14px',
                  height: '1px',
                  background: 'currentColor',
                  opacity: isDarkBackground ? 0.4 : 0.55,
                }}
              />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  )
}
